/**
 * Business timezone utilities.
 *
 * LeadForge is used by Rock River (America/Chicago). All appointment
 * date/time I/O MUST be anchored to the business timezone so a technician
 * on a phone in a different timezone (or a test in UTC) cannot drift the
 * stored instant off the owner's calendar.
 *
 * Rules of thumb:
 *   - UI pickers (datetime-local, hour+minute dropdowns) represent WALL
 *     CLOCK time in the business timezone. Convert → UTC ISO before POST.
 *   - Display of stored UTC ISO ALWAYS renders in the business timezone,
 *     with a "CT" indicator so the owner can visually verify.
 */

export const BUSINESS_TZ = "America/Chicago";

/**
 * Given a wall-clock in `tz`, return the UTC Date that represents it.
 * DST-safe: measures the actual offset at the proposed instant.
 */
function wallClockInZoneToUtc(y, m, d, hh, mm, ss, tz) {
  const guessUtc = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(
    dtf.formatToParts(new Date(guessUtc)).map((p) => [p.type, p.value])
  );
  const h = parts.hour === "24" ? 0 : Number(parts.hour);
  const zoneWallAsUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    h, Number(parts.minute), Number(parts.second),
  );
  const offsetMs = zoneWallAsUtc - guessUtc; // zone offset at this instant
  return new Date(guessUtc - offsetMs);
}

/**
 * Compose a UTC ISO string from a calendar-picker Date (whose Y/M/D are
 * read as wall-clock parts) plus an explicit hour/minute, interpreted
 * strictly as business-timezone wall clock.
 *
 * Browser timezone does NOT influence the result.
 */
export function composeBusinessIso(dateObj, hour, minute) {
  if (!dateObj) return null;
  const y = dateObj.getFullYear();
  const m = dateObj.getMonth() + 1;
  const d = dateObj.getDate();
  return wallClockInZoneToUtc(
    y, m, d, Number(hour) || 0, Number(minute) || 0, 0, BUSINESS_TZ,
  ).toISOString();
}

/**
 * Convert a UTC ISO string → the string shape that an <input type="datetime-local">
 * expects, with the wall-clock projected into the business timezone.
 */
export function isoToBusinessInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: BUSINESS_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(dtf.formatToParts(d).map((p) => [p.type, p.value]));
  const h = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${h}:${parts.minute}`;
}

/**
 * Convert a datetime-local string "YYYY-MM-DDTHH:MM" (interpreted as
 * business-timezone wall clock) → UTC ISO string.
 */
export function businessInputToIso(local) {
  if (!local) return null;
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm, ss] = timePart.split(":").map(Number);
  const utc = wallClockInZoneToUtc(y, m, d, hh, mm, ss || 0, BUSINESS_TZ);
  return utc.toISOString();
}

/** Pretty display helpers — always Chicago, always labelled "CT". */
export function formatBusinessDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const s = d.toLocaleString("en-US", {
    timeZone: BUSINESS_TZ,
    weekday: "short", month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
  return `${s} CT`;
}

export function formatBusinessTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    timeZone: BUSINESS_TZ,
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

export function formatBusinessDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    timeZone: BUSINESS_TZ,
    month: "short", day: "numeric", year: "numeric",
  });
}

/**
 * Day-key in business timezone — "YYYY-MM-DD". Use this to group / filter
 * jobs by calendar day instead of comparing browser-local getDate().
 */
export function businessDayKey(iso) {
  if (!iso) return "";
  const i = isoToBusinessInput(iso);
  return i ? i.slice(0, 10) : "";
}

/**
 * Day-key for a plain Date object whose wall-clock parts should be treated
 * as business-timezone (i.e. a calendar cell the user clicked). We only
 * read Y/M/D from the Date and ignore the browser-local time component.
 */
export function dateObjToBusinessDayKey(dateObj) {
  if (!dateObj) return "";
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
